import { Strategy } from "src/common/Strategy";
import { z, ZodType } from "zod";
import { TreeLikeData } from "src/types/TreeLikeData";
import { zUid } from "src/types/zUid";
import { updateNodeRecursively, walk } from "src/components/TreeStd";
import { EntityFilterTreeData, zEntityFilterValue } from "./AttsTree/types";

enum Versions {
  DEPRECATED_1 = "1",
  LATEST = "2",
}

const zFilterItemTreeData: ZodType<TreeLikeData<EntityFilterTreeData>> = z.lazy(
  () =>
    z.object({
      attrId: zUid,
      filterValue: zEntityFilterValue,
      children: zFilterItemTreeData.array().optional(),
      key: z.string(),
    }),
);

const zEntitySearchStateDataV1 = z.object({
  attsOrder: z.string().array(),
  attsSelected: z.string().array(),
  treeSlected: z.object({
    treeData: zFilterItemTreeData.array(),
    selected: z.string().array(),
    expanded: z.string().array(),
  }),
});

export type ZEntitySearchStateDataV1 = z.infer<typeof zEntitySearchStateDataV1>;

const zEntitySearchStateV1 = z.object({
  version: z.literal(Versions.DEPRECATED_1),
  data: zEntitySearchStateDataV1,
});
const zEntitySearchStateV2 = z.object({
  version: z.literal(Versions.LATEST),
  data: zEntitySearchStateDataV1,
});
const zEntityFilterStateConfig = z.discriminatedUnion("version", [
  zEntitySearchStateV1,
  zEntitySearchStateV2,
]);

export type ZEntityFilterStateConfig = z.infer<typeof zEntityFilterStateConfig>;

export const getLatestEntityFilterState = (
  params: ZEntityFilterStateConfig["data"],
): ZEntityFilterStateConfig => {
  const settings = zEntityFilterStateConfig.parse({
    version: Versions.LATEST,
    data: params,
  });
  return settings;
};

/**
 * схема данных, с которой работает иерархический фильтр изменилась,
 * но в user-preferences до сих пор могут лежать старые версии = 1, у которых toSearch - это строка, а не массив.
 * Поэтому если обнаружена старая версия, мы приводим ее к новой, чтобы не возникло ошибок.
 */
const t1to2 = (
  data: ZEntityFilterStateConfig["data"],
): ZEntityFilterStateConfig => {
  const treeDataNew = data.treeSlected.treeData;
  walk((node) => {
    if (
      typeof node.filterValue?.toSearch !== "undefined" &&
      !Array.isArray(node.filterValue.toSearch)
    ) {
      updateNodeRecursively(treeDataNew, node.key, {
        ...node,
        filterValue: {
          ...node.filterValue,
          toSearch: [node.filterValue.toSearch],
        },
      });
    }
  }, treeDataNew);

  return {
    version: Versions.LATEST,
    data: zEntitySearchStateDataV1.parse({
      ...data,
      treeSlected: { ...data.treeSlected, treeData: treeDataNew },
    }),
  };
};
/**
 * Все настройки должны быть приведены к совместимости с последней версией
 */
const transform2Latest: Record<
  ZEntityFilterStateConfig["version"],
  (data: ZEntityFilterStateConfig["data"]) => ZEntityFilterStateConfig
> = {
  "1": t1to2,
  "2": (params) => ({
    version: Versions.LATEST,
    data: zEntitySearchStateDataV1.parse(params),
  }),
};

export class EntityFilterStateStrategy
  implements Strategy<ZEntityFilterStateConfig>
{
  state?: ZEntityFilterStateConfig;

  bind(params?: ZEntityFilterStateConfig): void {
    if (!params) return;
    const { version, data } = params;
    const transform = transform2Latest[version];
    if (!transform) throw Error(`Unknown version ${version}`);
    this.state = transform(data);
  }

  execute(): ZEntityFilterStateConfig {
    return (
      this.state ??
      getLatestEntityFilterState({
        attsOrder: [],
        attsSelected: [],
        treeSlected: {
          treeData: [],
          selected: [],
          expanded: [],
        },
      })
    );
  }
}
